import type { UIMessageChunk } from "ai";

/**
 * Defensive repair for UI message chunk streams whose part framing was lost.
 *
 * The AI SDK's `processUIMessageStream` hard-errors when it sees a
 * `text-delta` / `text-end` (or reasoning equivalent) for a part id it
 * never received a `*-start` for:
 *
 *   "Received text-delta for missing text part with ID "0". Ensure a
 *    "text-start" chunk is sent before any "text-delta" chunks."
 *
 * In our resumable-stream architecture that happens when a part's
 * `*-start` is unavailable on the wire — a dropped write at the
 * stream-store layer, a replay window that opens past the start chunk, or
 * an off-by-one between the client's chunk cursor and the server's. The
 * turn's *content* is still flowing; failing the whole stream over a
 * missing ~20-byte start marker turns a cosmetic gap into a user-facing
 * error toast.
 *
 * CRITICAL: this transform must mirror the AI SDK's part-lifetime state
 * machine exactly, because the SDK resets its active-part map on **every**
 * `finish-step` (`activeTextParts = {}` / `activeReasoningParts = {}`),
 * not just on a new message. Our turns are multi-step and reuse the same
 * part id (e.g. `"0"`) for the text part in *each* step:
 *
 *   step1: text-start(0) … text-end(0) finish-step
 *   step2: text-start(0) … text-end(0) finish-step   ← same id, new part
 *
 * If a non-first step's `text-start(0)` is dropped in transit, a sanitizer
 * that tracked "started" ids globally (across the whole message) would
 * still think `0` is open and refuse to synthesize — while the SDK, having
 * cleared its map at the previous `finish-step`, throws "missing text
 * part". Resetting tracking on `finish-step` keeps the two state machines
 * aligned so we synthesize a start exactly when (and only when) the SDK
 * would otherwise error.
 *
 * The transform tracks, per step, which part ids are open (a start was
 * seen or synthesized, no end yet) and which were already ended, then:
 *   - synthesizes a missing `*-start` for an orphaned delta/end, and
 *   - drops a re-delivered `*-start`/`*-delta`/`*-end` for a part that is
 *     already open / already ended *within the same step* (reconnect
 *     overlap), so the replay is idempotent.
 * Tracking resets on each `start` (new message) and each `finish-step`
 * (new step), so legitimate reuse of a part id in a later step is never
 * mistaken for a duplicate. Well-formed streams pass through untouched;
 * the worst case becomes "the visible text begins slightly into the step"
 * or "a duplicated tail is silently dropped" instead of a dead turn.
 */
export function sanitizeChunkStream(
  chunks: AsyncIterable<UIMessageChunk>,
  options?: SanitizeOptions
): AsyncGenerator<UIMessageChunk, void, void> {
  return sanitize(chunks, options);
}

export type SanitizeOptions = {
  /**
   * Drop tool chunks whose `toolCallId` never had a part-creating chunk
   * (`tool-input-start` / `tool-input-available` / `tool-input-error`) in
   * this stream. The SDK hard-errors the turn on such orphans ("No tool
   * invocation found for tool call ID …"), so on streams that build the
   * assistant message from scratch — a fresh send, where the SDK starts
   * with an empty parts array — dropping degrades a lost input chunk to
   * one missing tool card instead of a dead turn.
   *
   * MUST stay off for resume/reconnect streams: there the SDK seeds its
   * state from the client's cached assistant message, so a tool part
   * delivered before the disconnect is legitimately present even though
   * this replay window never carried its input chunk — dropping its
   * output would strand a real tool card at "running".
   */
  dropOrphanedToolChunks?: boolean;
};

async function* sanitize(
  chunks: AsyncIterable<UIMessageChunk>,
  options?: SanitizeOptions
): AsyncGenerator<UIMessageChunk, void, void> {
  const dropOrphanedToolChunks = options?.dropOrphanedToolChunks ?? false;
  let openText = new Set<string>();
  let endedText = new Set<string>();
  let openReasoning = new Set<string>();
  let endedReasoning = new Set<string>();
  // Tool calls whose part exists in the SDK's message state. Unlike text /
  // reasoning parts, tool parts are NOT cleared at `finish-step` — the SDK
  // looks them up across the whole accumulated message — so this only
  // resets on `start` (new message).
  let seenToolCalls = new Set<string>();

  const resetStepState = () => {
    // The SDK clears its active-part maps at message start and at every
    // step boundary; mirror that so a part id reused in the next step is
    // treated as brand new (and a dropped start for it gets synthesized).
    openText = new Set();
    endedText = new Set();
    openReasoning = new Set();
    endedReasoning = new Set();
  };

  for await (const chunk of chunks) {
    switch (chunk.type) {
      case "start":
        // New message — part ids restart.
        resetStepState();
        seenToolCalls = new Set();
        break;
      case "finish-step":
        // New step — the SDK drops all active parts here, so reused ids in
        // the next step must be tracked from scratch.
        resetStepState();
        break;
      case "text-start":
        // Re-delivered start for a part already open/ended this step
        // (reconnect overlap) — drop it; re-opening makes the SDK push a
        // duplicate part and reject the later end.
        if (openText.has(chunk.id) || endedText.has(chunk.id)) {
          continue;
        }
        openText.add(chunk.id);
        break;
      case "text-delta":
        // Delta replayed after the part was closed this step: its content
        // already streamed, so drop rather than reopen a closed part.
        if (endedText.has(chunk.id)) {
          continue;
        }
        if (!openText.has(chunk.id)) {
          openText.add(chunk.id);
          yield { type: "text-start", id: chunk.id };
        }
        break;
      case "text-end":
        // Duplicate end for an already-closed part — drop; a second end on a
        // part the SDK already removed throws "missing text part".
        if (endedText.has(chunk.id)) {
          continue;
        }
        if (!openText.has(chunk.id)) {
          yield { type: "text-start", id: chunk.id };
        }
        openText.delete(chunk.id);
        endedText.add(chunk.id);
        break;
      case "reasoning-start":
        if (openReasoning.has(chunk.id) || endedReasoning.has(chunk.id)) {
          continue;
        }
        openReasoning.add(chunk.id);
        break;
      case "reasoning-delta":
        if (endedReasoning.has(chunk.id)) {
          continue;
        }
        if (!openReasoning.has(chunk.id)) {
          openReasoning.add(chunk.id);
          yield { type: "reasoning-start", id: chunk.id };
        }
        break;
      case "reasoning-end":
        if (endedReasoning.has(chunk.id)) {
          continue;
        }
        if (!openReasoning.has(chunk.id)) {
          yield { type: "reasoning-start", id: chunk.id };
        }
        openReasoning.delete(chunk.id);
        endedReasoning.add(chunk.id);
        break;
      // Part-creating tool chunks: the SDK creates (or upserts) the tool
      // part for these, so the id is safe for later output/approval chunks.
      case "tool-input-start":
      case "tool-input-available":
      case "tool-input-error":
        seenToolCalls.add(chunk.toolCallId);
        break;
      // Orphan-sensitive tool chunks: the SDK throws when the referenced
      // part is missing. On from-scratch streams that means the input chunk
      // was lost in transit — drop the orphan instead of killing the turn.
      case "tool-input-delta":
      case "tool-output-available":
      case "tool-output-error":
      case "tool-output-denied":
      case "tool-approval-request":
        if (dropOrphanedToolChunks && !seenToolCalls.has(chunk.toolCallId)) {
          continue;
        }
        break;
      default:
        break;
    }
    yield chunk;
  }
}
